import { Fragment } from "react";
import { useTranslation } from "@1f/react-sdk";
import Grid from "@vapor/react-material/Grid";
import Stack from "@vapor/react-material/Stack";
import Autocomplete from "@vapor/react-material/Autocomplete";
import TextField from "@vapor/react-material/TextField";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { AdapterDateFns } from "@vapor/react-x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@vapor/react-x-date-pickers/LocalizationProvider";
import { DatePicker } from "@vapor/react-x-date-pickers/DatePicker";
import { it } from "date-fns/locale";
import { isDateValid } from "../../businessLogic/helpers/dateHelpers";
import { CervedDiffIcon } from "../CervedDiffIcon";

export const AnagraficaGeneralitaSection = ({
  currentAnagrafica,
  isPG,
  clienteAnagType,
  baseGenereList,
  tenantTypeId,
  txtImportoRef,
  isCessato,
  isNotLastVersion,
  onChange,
  onBlurPIva,
  onDateChange,
  onImportoChange,
  differences
}) => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <ExtendedTypography variant="titleSmall" color="primary.main">
        {t("anagrafica.anagraficaForm.gen")}
      </ExtendedTypography>

      {!isPG || clienteAnagType === "DI" ? (
        <Grid container>
          <Grid item xs={5} sx={{ pr: 2 }}>
            <TextField
              id="txtNome"
              label="Nome"
              defaultValue={currentAnagrafica.nome}
              size="small"
              fullWidth
              required
              onChange={onChange}
              onBlur={onBlurPIva}
              inputProps={{ autocomplete: "nope", maxLength: 100 }}
              readOnly={isCessato || isNotLastVersion}
            />
          </Grid>
          <Grid item xs={5} sx={{ pr: 2 }}>
            <TextField
              id="txtCognome"
              label="Cognome"
              defaultValue={currentAnagrafica.cognome}
              size="small"
              fullWidth
              required
              onChange={onChange}
              onBlur={onBlurPIva}
              inputProps={{ autocomplete: "nope", maxLength: 100 }}
              readOnly={isCessato || isNotLastVersion}
            />
          </Grid>
          <Grid item xs={2}>
            <Autocomplete
              id="ddlGenere"
              size="small"
              options={baseGenereList}
              sx={{ width: "100%" }}
              fullWidth
              onChange={onChange}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.value}
              value={
                baseGenereList.find(
                  (item) => item.id === currentAnagrafica.genereId
                ) ?? null
              }
              autoSelect
              autoHighlight
              blurOnSelect
              openOnFocus
              handleHomeEndKeys
              selectOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Genere"
                  required
                  sx={{ lineHeight: 1 }}
                />
              )}
              readOnly={isCessato || isNotLastVersion}
            />
          </Grid>
          {clienteAnagType === "DI" && (
            <Grid item xs={12} sx={{ pt: 1 }}>
              <TextField
                id="txtDenominazione"
                label={
                  <>
                    <CervedDiffIcon diff={differences?.denominazione} />
                    Denominazione
                  </>
                }
                defaultValue={currentAnagrafica.denominazione}
                size="small"
                fullWidth
                required={isPG}
                onChange={onChange}
                inputProps={{ autocomplete: "nope", maxLength: 255 }}
                readOnly={isCessato || isNotLastVersion}
              />
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid container>
          <Grid item xs={tenantTypeId === 3 ? 8 : 12}>
            <TextField
              id="txtDenominazione"
              label={
                <>
                  <CervedDiffIcon diff={differences?.denominazione} />
                  Denominazione
                </>
              }
              defaultValue={currentAnagrafica.denominazione}
              size="small"
              fullWidth
              required={isPG}
              onChange={onChange}
              inputProps={{ autocomplete: "nope", maxLength: 255 }}
              readOnly={isCessato || isNotLastVersion}
            />
          </Grid>
          {tenantTypeId === 3 ? (
            <Grid item xs={4} sx={{ pl: 2 }}>
              <TextField
                id="txtSocialCapital"
                label="Capitale Sociale in €"
                size="small"
                ref={txtImportoRef}
                defaultValue={currentAnagrafica.capSociale.toLocaleString("it-IT", {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })}
                fullWidth
                onChange={onImportoChange}
                inputProps={{ autocomplete: "nope" }}
                readOnly={isCessato || isNotLastVersion}
              />
            </Grid>
          ) : null}
        </Grid>
      )}

      {!isPG ? (
        <Fragment>
          <Stack direction="row" spacing={2}>
            <TextField
              id="txtProfessione"
              label="Professione"
              defaultValue={currentAnagrafica.professione}
              size="small"
              fullWidth
              required
              onChange={onChange}
              inputProps={{ autocomplete: "nope", maxLength: 255 }}
              readOnly={isCessato || isNotLastVersion}
            />
            <TextField
              id="txtNatoA"
              label="Nato a"
              defaultValue={currentAnagrafica.nascitaLuogo}
              size="small"
              fullWidth
              required
              onChange={onChange}
              onBlur={onBlurPIva}
              inputProps={{ autocomplete: "nope", maxLength: 255 }}
              readOnly={isCessato || isNotLastVersion}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
              <DatePicker
                label="Nato il"
                closeOnSelect
                format="dd/MM/yyyy"
                mask="__/__/____"
                maxDate={new Date()}
                value={
                  isDateValid(currentAnagrafica.nascitaData)
                    ? new Date(currentAnagrafica.nascitaData)
                    : null
                }
                onChange={onDateChange}
                slotProps={{
                  textField: {
                    required: true,
                    placeholder: "dd/MM/yyyy",
                    size: "small",
                    fullWidth: true,
                  },
                }}
                readOnly={isCessato || isNotLastVersion}
              />
            </LocalizationProvider>
          </Stack>
        </Fragment>
      ) : (
        <Fragment>
          <Stack direction="row" spacing={2}>
            <TextField
              id="txtReaComune"
              label="Iscritta al R.I. di"
              defaultValue={currentAnagrafica.reaComune}
              size="small"
              fullWidth
              onChange={onChange}
              inputProps={{ autocomplete: "nope", maxLength: 255 }}
              readOnly={isCessato || isNotLastVersion}
            />
            <TextField
              id="txtReaN"
              label="REA N."
              defaultValue={currentAnagrafica.reaN}
              size="small"
              fullWidth
              onChange={onChange}
              inputProps={{ autocomplete: "nope", maxLength: 50 }}
              readOnly={isCessato || isNotLastVersion}
            />
          </Stack>
        </Fragment>
      )}

      {isPG && tenantTypeId === 3 ? (
        <Stack spacing={1}>
          <TextField
            id="txtorgAmmin"
            label="Dati dei componenti dell'organo amministrativo"
            size="small"
            defaultValue={currentAnagrafica.orgAmmin}
            fullWidth
            multiline
            onChange={onChange}
            inputProps={{ autocomplete: "nope", maxLength: 1024 }}
            readOnly={isCessato || isNotLastVersion}
          />
        </Stack>
      ) : null}
    </Fragment>
  );
};
